import { useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useFeature } from '../featureFlags/FeatureGate';
import { useHasPermission } from '../PermissionGate';
import { toErrorMessage } from './orderActionsApi';

/**
 * „Obsadź mimo blokady” — Team Leader (odpowiedzi Marathonu/Euvic
 * 16.10.2026, pyt. 54e: „przy blokadzie na pojeździe przypisać ręcznie może
 * tylko Team Leader”; przełącznik `assignDespiteBlock`).
 *
 * Gdy obsadzenie kończy się odmową 409, w której stoją **same** nieważne
 * uprawnienia typu Blokada (API: `overridable: true`), a konto ma
 * uprawnienie `ASSIGN_DESPITE_BLOCK`, zamiast komunikatu błędu otwiera się
 * okno z listą blokad i polem powodu (co najmniej 10 znaków). Potwierdzenie
 * ponawia to samo obsadzenie z `overrideBlocking: true`. Bez uprawnienia
 * albo z przeszkodą nie do obejścia (ładowność, ADR, auto poza eksploatacją,
 * nieważne zezwolenie na pracę kierowcy — 19.10.2026) — komunikat jak dotąd. Rozstrzyga zawsze API; okno tylko nie każe klikać
 * w coś, czego serwer i tak odmówi.
 *
 * Team Leader to planista główny FTL — decyzja użytkownika 19.10.2026;
 * obchodzą też administratorzy — decyzja użytkownika 20.10.2026 („admin ma
 * wszystkie możliwości”; mapa uprawnień w API). Auto niedostępne w TMS
 * i nieważnego zezwolenia na pracę kierowcy (decyzja użytkownika
 * 19.10.2026) obejść się nie da.
 */

export const FEATURE_ASSIGN_DESPITE_BLOCK = 'assignDespiteBlock';

/**
 * Szersze obejście (21.10.2026, S-21.10-24, `assignOverrideWider`): Team
 * Leader obchodzi także za małą ładowność i auto poza eksploatacją w TMS —
 * okno nie mówi wtedy „nieważne uprawnienia”, tylko wylicza, co blokuje.
 */
export const FEATURE_ASSIGN_OVERRIDE_WIDER = 'assignOverrideWider';

/** Nagłówek listy blokad w oknie — przy szerszym obejściu ogólny. */
export const OVERRIDE_LIST_INTRO = 'Obsadzenie blokują nieważne uprawnienia z kartoteki:';
export const OVERRIDE_LIST_INTRO_WIDER = 'Obsadzenie blokuje:';

/** Najkrótszy powód — jak w API (`MIN_OVERRIDE_REASON_LENGTH`). */
export const MIN_OVERRIDE_REASON_LENGTH = 10;

/** Odmowa, którą da się obejść: zdanie z API i lista blokad. */
export interface OverridableRefusal {
  message: string;
  blockers: string[];
}

type RefusalBody = {
  response?: {
    status?: number;
    data?: { message?: unknown; blockers?: unknown; overridable?: unknown };
  };
};

/** Odmowa 409 z samymi blokadami z uprawnień — albo `null`. */
export function overridableRefusal(err: unknown): OverridableRefusal | null {
  const response = (err as RefusalBody)?.response;
  const data = response?.data;
  if (response?.status !== 409 || data?.overridable !== true) return null;
  const blockers = Array.isArray(data.blockers)
    ? data.blockers.filter((b): b is string => typeof b === 'string')
    : [];
  if (blockers.length === 0) return null;
  return { message: toErrorMessage(err), blockers };
}

/** Ponowienie obsadzenia z powodem obejścia. */
export type OverrideRetry = (reason: string) => Promise<void>;

/**
 * Hak dla każdego miejsca, które obsadza („Obsadź” w rekomendacjach,
 * „Obsadź…” w wierszu banku, „Obsadź” / „Zmień pojazd” na karcie).
 * `offer(err, retry)` — `true`, gdy otworzył okno (wywołujący nie pokazuje
 * wtedy błędu); `dialog` trzeba wyrenderować obok.
 */
export function useAssignOverride(): {
  offer: (err: unknown, retry: OverrideRetry) => boolean;
  dialog: ReactNode;
} {
  const on = useFeature(FEATURE_ASSIGN_DESPITE_BLOCK);
  const permitted = useHasPermission('ASSIGN_DESPITE_BLOCK');
  const [pending, setPending] = useState<{
    refusal: OverridableRefusal;
    retry: OverrideRetry;
  } | null>(null);

  const offer = (err: unknown, retry: OverrideRetry): boolean => {
    if (!on || !permitted) return false;
    const refusal = overridableRefusal(err);
    if (refusal === null) return false;
    setPending({ refusal, retry });
    return true;
  };

  const dialog = pending ? (
    <AssignOverrideDialog
      blockers={pending.refusal.blockers}
      onCancel={() => setPending(null)}
      onConfirm={async (reason) => {
        await pending.retry(reason);
        setPending(null);
      }}
    />
  ) : null;

  return { offer, dialog };
}

export const AssignOverrideDialog = ({
  blockers,
  onCancel,
  onConfirm,
}: {
  blockers: string[];
  onCancel: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wider = useFeature(FEATURE_ASSIGN_OVERRIDE_WIDER);
  const trimmed = reason.trim();
  const tooShort = trimmed.length < MIN_OVERRIDE_REASON_LENGTH;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(trimmed);
    } catch (err) {
      setError(toErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Obsadź mimo blokady</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {wider ? OVERRIDE_LIST_INTRO_WIDER : OVERRIDE_LIST_INTRO}
        </Typography>
        <ul data-testid="override-blockers">
          {blockers.map((b) => (
            <li key={b}>
              <Typography variant="body2">{b}</Typography>
            </li>
          ))}
        </ul>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Masz uprawnienie Team Leadera — możesz obsadzić mimo blokady. Powód,
          Twoje konto i obejście blokady trafią do historii zmian zlecenia.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          size="small"
          label="Powód obsadzenia mimo blokady"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={busy}
          helperText={`Co najmniej ${MIN_OVERRIDE_REASON_LENGTH} znaków`}
          // Kontrakt identyfikatorów paczki p. Mariusza 21.10 (`dlg-powod`).
          slotProps={{ htmlInput: { 'data-testid': 'dlg-powod' } }}
        />
        {error ? (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Anuluj
        </Button>
        <Button
          variant="contained"
          color="warning"
          data-testid="dlg-ok"
          disabled={busy || tooShort}
          onClick={() => void confirm()}
        >
          Obsadź mimo blokady
        </Button>
      </DialogActions>
    </Dialog>
  );
};
